import { formatCurrency, getFormattingLocaleForRegionCode } from 'lib/format/formatCurrencyIntl'
import type { LoyaltyEarnTotals } from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import type { OfferPageAssistantPriceFormatContext } from './offerPageContextTypes'

const DISPLAY_CONTEXT_FIELD_MAX_LENGTH = 512
const DISPLAY_CONTEXT_BODY_MAX_LENGTH = 4000

interface DisplayedPriceSummaryOptions {
  standardTotal?: number
  memberTotal?: number
  valuedUpTo?: number
  showValuedUpTo?: boolean
}

export function nonEmptyString(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim()
  return trimmed || undefined
}

function boundedNonEmptyString(value: string | undefined | null, maxLength: number): string | undefined {
  const normalized = nonEmptyString(value)
  if (!normalized) {
    return undefined
  }

  return normalized.length > maxLength ? nonEmptyString(normalized.slice(0, maxLength)) : normalized
}

export function displayContextField(value: string | undefined | null): string | undefined {
  return boundedNonEmptyString(value, DISPLAY_CONTEXT_FIELD_MAX_LENGTH)
}

export function displayContextBody(value: string | undefined | null): string | undefined {
  return boundedNonEmptyString(value, DISPLAY_CONTEXT_BODY_MAX_LENGTH)
}

function isFiniteAmount(amount: number | undefined): amount is number {
  return amount !== undefined && Number.isFinite(amount)
}

function formatTotalPrice(
  amount: number,
  { currencyCode, regionCode }: OfferPageAssistantPriceFormatContext,
): string {
  return `${formatCurrency(amount, currencyCode, regionCode, { format: 'roundedDollar' })} total`
}

export function formatEarnNumber(value: number, regionCode: string): string {
  return new Intl.NumberFormat(getFormattingLocaleForRegionCode(regionCode)).format(value)
}

export function formatOfferPageLoyaltyEarnSummary(
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
  regionCode: string,
): string | undefined {
  if (!loyaltyEarnTotals || (loyaltyEarnTotals.points <= 0 && loyaltyEarnTotals.statusCredits <= 0)) {
    return undefined
  }

  const summaryParts = loyaltyEarnTotals.points > 0 ?
      [`${formatEarnNumber(loyaltyEarnTotals.points, regionCode)} Société points`] :
      []
  if (loyaltyEarnTotals.statusCredits > 0) {
    summaryParts.push(`${formatEarnNumber(loyaltyEarnTotals.statusCredits, regionCode)} status credits`)
  }

  return `Earn from ${summaryParts.join(' + ')}`
}

export function buildDisplayedPriceSummary(
  {
    standardTotal,
    memberTotal,
    valuedUpTo,
    showValuedUpTo = true,
  }: DisplayedPriceSummaryOptions,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
): string | undefined {
  const priceParts: Array<string> = []
  const payableTotals: Array<number> = []
  const hasStandardTotal = isFiniteAmount(standardTotal)
  const hasMemberTotal = isFiniteAmount(memberTotal) && memberTotal > 0 && memberTotal !== standardTotal

  if (hasMemberTotal) {
    priceParts.push(`LuxPlus+ member ${formatTotalPrice(memberTotal, priceFormatContext)}`)
    payableTotals.push(memberTotal)
  }

  if (hasStandardTotal) {
    const label = hasMemberTotal ? 'non-member ' : ''
    priceParts.push(`${label}${formatTotalPrice(standardTotal, priceFormatContext)}`)
    payableTotals.push(standardTotal)
  }

  const lowestPayableTotal = payableTotals.length ? Math.min(...payableTotals) : undefined
  if (
    showValuedUpTo &&
    isFiniteAmount(valuedUpTo) &&
    valuedUpTo > 0 &&
    (lowestPayableTotal === undefined || valuedUpTo > lowestPayableTotal)
  ) {
    priceParts.push(`valued up to ${formatCurrency(valuedUpTo, priceFormatContext.currencyCode, priceFormatContext.regionCode, { format: 'roundedDollar' })}`)
  }

  return priceParts.length ? priceParts.join('; ') : undefined
}

export function formatPriceSummary(
  amount: number | undefined,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
): string | undefined {
  return buildDisplayedPriceSummary({ standardTotal: amount }, priceFormatContext)
}
